import React, { forwardRef, useImperativeHandle } from 'react'
import styled, { css } from 'styled-components'
import { useLogic } from '~common/ui-logic/hooks/logic'
import { TextField } from '~common/components/text-field'
import { SearchComponentLogic, SearchComponentLogicDependencies } from './logic'
import { SortDirection, SortIndicator } from '~common/components/sort-indicator'
import { useAppContext } from '~/app-context'

export interface SearchComponentProps extends SearchComponentLogicDependencies {
    placeholder?: string
    initActive?: boolean
    initValue?: string
    icon?: string
    onClick?: () => void
    onConfirm?: (value: string) => void
    onSortClick?: () => void
    sortDirection?: SortDirection
    width?: string
    size?: 'small' | 'medium' | 'large'
    disableOnBlur?: boolean
    autoFocus?: boolean
    context?: 'match-search' | 'header'
}

export interface SearchComponentRef {
    getValue: () => string
}

export const SearchComponent = forwardRef<
    SearchComponentRef,
    SearchComponentProps
>((props, ref) => {
    const appContext = useAppContext()
    const { logic, state } = useLogic(SearchComponentLogic, {
        ...props,
        ...appContext,
    })

    useImperativeHandle(ref, () => ({
        getValue: () => state.searchQuery,
    }))

    const handleChange = (value: string) => {
        logic.handleSearchChange(value)
    }

    return (
        <SearchContainer width={props.width ?? '300px'}>
            <TextField
                placeholder={props.placeholder || 'Search...'}
                editable={true}
                onChange={handleChange}
                icon={props.icon}
                size={props.size}
                onConfirm={(value: string) => props.onConfirm?.(value)}
                onClear={() => handleChange('')}
                width={'100%'}
                initActive={props.initActive}
                initValue={state.searchQuery}
                disableOnBlur={props.disableOnBlur}
                autoFocus={props.autoFocus}
                actionButtons={[
                    ...(props.onSortClick
                        ? [
                              <SortIndicator
                                  key="sort"
                                  initialDirection={
                                      props.sortDirection || 'asc'
                                  }
                                  onSortChange={props.onSortClick}
                              />,
                          ]
                        : []),
                ]}
            />
        </SearchContainer>
    )
})

const SearchContainer = styled.div<{ width?: string }>`
    width: ${({ width }) => width};
`
